import { useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles, Paperclip, MessageSquare, History as HistoryIcon, Calendar, Trash2 } from "lucide-react";
import Icon from "@/components/Icon";
import { getEntity } from "@/lib/registry";
import { can } from "@/lib/permissions";
import { StatusBadge, PriorityBadge, TagList, Avatar, relTime } from "@/components/library/badges";
import { EmptyState, LoadingState } from "@/components/library/states";
import { ConfirmDelete } from "@/components/shared/ConfirmDelete";

// Generic entity card — driven by registry metadata. No business logic.
export function EntityCard({ entity, onOpen, testid }) {
  const cfg = getEntity(entity.entity_type);
  const caps = cfg.capabilities || {};
  return (
    <button onClick={() => onOpen?.(entity)} data-testid={testid || `entity-card-${entity.id}`}
      className="text-left w-full bg-card border border-border rounded-2xl p-5 hover:border-[hsl(var(--border-hover))] hover:-translate-y-0.5 transition-all duration-300">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="h-8 w-8 grid place-items-center rounded-lg text-white shrink-0" style={{ background: cfg.accent }}>
            <Icon name={undefined} className="hidden" />
            {cfg.icon && <cfg.icon className="h-4 w-4" strokeWidth={2} />}
          </span>
          <h3 className="font-display font-semibold tracking-tight truncate">{entity.title}</h3>
        </div>
        {caps.status && entity.status && <StatusBadge entityType={entity.entity_type} status={entity.status} />}
      </div>
      {entity.description && <p className="text-sm text-muted-foreground mt-3 line-clamp-2">{entity.description}</p>}
      <div className="flex items-center flex-wrap gap-2 mt-4">
        {entity.priority && <PriorityBadge priority={entity.priority} />}
        {entity.assignee && <span className="flex items-center gap-1.5 text-xs text-muted-foreground"><Avatar name={entity.assignee.name} size={20} />{entity.assignee.name}</span>}
        {entity.due_at && <span className="flex items-center gap-1 text-xs text-muted-foreground"><Calendar className="h-3 w-3" />{relTime(entity.due_at)}</span>}
        {caps.ai && entity.has_ai && <span className="flex items-center gap-1 text-xs text-accent"><Sparkles className="h-3 w-3" />AI</span>}
      </div>
      {caps.tags && entity.tags?.length > 0 && <div className="mt-3"><TagList tags={entity.tags} /></div>}
    </button>
  );
}

export function EntityList({ entities, onOpen, loading, emptyTitle = "Inget att visa", emptyHint, columns = 2, testid = "entity-list" }) {
  if (loading) return <LoadingState rows={3} />;
  if (!entities?.length) return <EmptyState title={emptyTitle} hint={emptyHint} />;
  const c = { 1: "grid-cols-1", 2: "sm:grid-cols-2", 3: "sm:grid-cols-2 lg:grid-cols-3" }[columns] || "sm:grid-cols-2";
  return (
    <div data-testid={testid} className={`grid gap-4 ${c}`}>
      {entities.map((e) => <EntityCard key={e.id} entity={e} onOpen={onOpen} />)}
    </div>
  );
}

// Dense, responsive table driven by registry fields.
export function EntityTable({ entityType, rows, onOpen, loading, testid = "entity-table" }) {
  const cfg = getEntity(entityType);
  if (loading) return <LoadingState rows={4} />;
  if (!rows?.length) return <EmptyState title="Inga rader" />;
  return (
    <div data-testid={testid} className="overflow-x-auto rounded-2xl border border-border bg-card">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-muted-foreground border-b border-border">
            <th className="px-4 py-3 font-medium">Titel</th>
            <th className="px-4 py-3 font-medium hidden sm:table-cell">Status</th>
            <th className="px-4 py-3 font-medium hidden md:table-cell">Ansvarig</th>
            <th className="px-4 py-3 font-medium hidden md:table-cell">Uppdaterad</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} onClick={() => onOpen?.(r)} data-testid={`entity-row-${r.id}`}
              className="border-b border-border last:border-0 hover:bg-secondary/50 cursor-pointer transition-colors">
              <td className="px-4 py-3 font-medium flex items-center gap-2">
                {cfg.icon && <cfg.icon className="h-4 w-4 text-muted-foreground" />}{r.title}
              </td>
              <td className="px-4 py-3 hidden sm:table-cell">{r.status ? <StatusBadge entityType={entityType} status={r.status} /> : "—"}</td>
              <td className="px-4 py-3 hidden md:table-cell text-muted-foreground">{r.assignee?.name || "—"}</td>
              <td className="px-4 py-3 hidden md:table-cell text-muted-foreground font-mono text-xs">{relTime(r.updated_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// EntityDrawer — composes capability-driven sections via slots. Permission-aware.
export function EntityDrawer({ open, entity, role = "personal", onClose, onDelete,
                              detail, comments, attachments, audit, ai, testid = "entity-drawer" }) {
  const [tab, setTab] = useState("detail");
  if (!entity) return null;
  const cfg = getEntity(entity.entity_type);
  const caps = cfg.capabilities || {};
  const canDelete = cfg.permissions?.delete && can(role, cfg.permissions.delete);

  const tabs = [
    { key: "detail", label: "Detaljer", icon: null, show: true, node: detail },
    { key: "comments", label: "Kommentarer", icon: MessageSquare, show: caps.comments, node: comments },
    { key: "attachments", label: "Bilagor", icon: Paperclip, show: caps.attachments, node: attachments },
    { key: "ai", label: "AI-analys", icon: Sparkles, show: caps.ai, node: ai },
    { key: "audit", label: "Historik", icon: HistoryIcon, show: caps.audit, node: audit },
  ].filter((t) => t.show);

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50" data-testid={testid}>
          <motion.div className="absolute inset-0 bg-black/50" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} />
          <motion.div className="absolute right-0 top-0 h-full w-full max-w-md bg-background border-l border-border flex flex-col"
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "tween", duration: 0.25, ease: [0.16, 1, 0.3, 1] }}>
            <div className="flex items-start gap-3 p-5 border-b border-border">
              <span className="h-9 w-9 grid place-items-center rounded-lg text-white shrink-0" style={{ background: cfg.accent }}>
                {cfg.icon && <cfg.icon className="h-4 w-4" strokeWidth={2} />}
              </span>
              <div className="flex-1 min-w-0">
                <p className="overline text-muted-foreground">{cfg.label}</p>
                <h2 className="font-display text-lg font-bold tracking-tight leading-snug">{entity.title}</h2>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  {caps.status && entity.status && <StatusBadge entityType={entity.entity_type} status={entity.status} />}
                  {entity.priority && <PriorityBadge priority={entity.priority} />}
                </div>
              </div>
              <button onClick={onClose} data-testid="drawer-close" className="text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
            </div>
            <div className="flex gap-1 px-3 border-b border-border overflow-x-auto no-scrollbar">
              {tabs.map((t) => (
                <button key={t.key} onClick={() => setTab(t.key)} data-testid={`drawer-tab-${t.key}`}
                  className={`flex items-center gap-1.5 px-3 py-2.5 text-sm whitespace-nowrap border-b-2 transition-colors ${
                    tab === t.key ? "border-accent text-foreground font-medium" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                  {t.icon && <t.icon className="h-3.5 w-3.5" />}{t.label}
                </button>
              ))}
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              {tabs.find((t) => t.key === tab)?.node || <p className="text-sm text-muted-foreground">Inget innehåll.</p>}
            </div>
            {canDelete && (
              <div className="p-4 border-t border-border">
                <ConfirmDelete
                  title="Ta bort posten?"
                  description="Posten tas bort och åtgärden loggas i historiken. Detta går inte att ångra."
                  onConfirm={() => onDelete?.(entity)}
                  trigger={
                    <button data-testid="drawer-delete"
                      className="flex items-center gap-2 text-sm text-[hsl(0_80%_72%)] hover:text-[hsl(0_80%_82%)]">
                      <Trash2 className="h-4 w-4" />Ta bort
                    </button>
                  }
                />
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
}

// EntityTimeline — lifecycle steps for a single entity.
export function EntityTimeline({ items = [], testid = "entity-timeline" }) {
  if (!items.length) return <EmptyState title="Ingen historik" />;
  return (
    <div data-testid={testid} className="relative pl-6">
      <div className="absolute left-[7px] top-2 bottom-2 w-px bg-border" />
      {items.map((it, i) => (
        <div key={i} className="relative pb-5">
          <div className="absolute -left-6 top-1 h-3.5 w-3.5 rounded-full bg-card border-2 border-accent" />
          <p className="text-sm">{it.label}</p>
          <p className="text-xs text-muted-foreground mt-0.5 font-mono">{it.actor ? `${it.actor} · ` : ""}{relTime(it.at)}</p>
        </div>
      ))}
    </div>
  );
}
